import { Direction } from '@modules/common/types';
import { removeTypeName } from '@modules/common/utils';
import { DASHBOARD_ACTIVE_CELL_ID } from '@modules/dashboard/constants';
import { addCellToDashboardLayout } from '@modules/dashboard/helpers';
import { useUpdateDashboard } from '@modules/dashboard/hooks/useUpdateDashboard';
import {
  DashboardAnalysisMetadataInput,
  DashboardLayout,
} from 'generated/graphql';
import Lockr from 'lockr';
import { cloneDeep } from 'lodash';
import { useCallback } from 'react';

interface useAddAnalysisToDashboardParams {
  dashboard: {
    id: string;
    viewToken: string;
    editToken: string;
    version?: number;
  };
  dashboardLayoutRowID?: string | null;
  dashboardLayout: DashboardLayout;
  analysisID: string;
  cellInsertDirection: Direction;
  analysesMetadata?: DashboardAnalysisMetadataInput[];
  shouldAddAnalysisInUpdateCall?: boolean;
  shouldRefetchAnalyses?: boolean;
  shouldSetAddedCellIDInLocalStorage?: boolean;
  analysisSettings?: {
    shouldShowAsFeed?: boolean;
  };
}

export function useAddAnalysisToDashboard() {
  const { updateDashboard, loading } = useUpdateDashboard();

  const addAnalysisToDashboard = useCallback(
    async ({
      dashboard,
      dashboardLayout,
      dashboardLayoutRowID,
      analysisID,
      cellInsertDirection,
      analysesMetadata,
      shouldAddAnalysisInUpdateCall = true, // Use false if we are using `dashboardToAddTo` field in createAnalysis request
      shouldRefetchAnalyses = false,
      shouldSetAddedCellIDInLocalStorage = false,
      analysisSettings,
    }: useAddAnalysisToDashboardParams) => {
      const clonedLayout = cloneDeep(dashboardLayout);
      removeTypeName(clonedLayout);

      const clonedAnalysesMetadata = cloneDeep(analysesMetadata ?? []);
      removeTypeName(clonedAnalysesMetadata);

      if (analysisSettings) {
        const analysisMetadata = clonedAnalysesMetadata?.find(
          (analysis) => analysis.id === analysisID,
        );

        if (analysisMetadata) {
          analysisMetadata.shouldShowAsFeed = analysisSettings.shouldShowAsFeed;
        } else {
          clonedAnalysesMetadata?.push({
            id: analysisID,
            shouldShowAsFeed: analysisSettings.shouldShowAsFeed,
          });
        }
      }

      const newCell = {
        id: `cell_${Date.now()}`,
        columnSpan: 12,
        contentID: analysisID,
      };

      addCellToDashboardLayout({
        cell: newCell,
        rowID: dashboardLayoutRowID,
        layout: clonedLayout,
        cellInsertDirection,
      });

      if (shouldSetAddedCellIDInLocalStorage) {
        Lockr.set(DASHBOARD_ACTIVE_CELL_ID, newCell.id);
      }

      await updateDashboard({
        updateRequest: {
          id: dashboard.id,
          viewToken: dashboard.viewToken,
          editToken: dashboard.editToken,
          version: dashboard.version,
          metadataV2: {
            layout: clonedLayout,
            analysesMetadata: clonedAnalysesMetadata,
          },
          addAnalysisIDs: shouldAddAnalysisInUpdateCall
            ? [analysisID]
            : undefined,
        },
        shouldRefetchAnalyses,
      });
    },
    [updateDashboard],
  );

  return [addAnalysisToDashboard, { loading }] as const;
}
